// @ts-strict-ignore
import { Component, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { ErrorCheck } from "src/app/shared/models/error-check.model";
import { ProgressCheck } from "src/app/shared/models/progress-check.model";
import * as C from "../../../../../../shared/constants/globals.constants";
import { EWizDynamicBaseClassComponent } from "src/app/modules/esim/shared/classes/ewiz-dynamic.class";
import { DataService } from "src/app/shared/services/data.service";
import * as ProfileModel from "src/app/shared/models/profile.model";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { MultiSelectModule } from "primeng/multiselect";
import { SelectModule } from "primeng/select";
import { TableModule } from "primeng/table";
import { SharedDirectivesModule } from "src/app/shared/shared-directives.module";
import { ErrorMessageComponent } from "src/app/modules/esim/shared/components/error-message/error-message.component";

/**
 * Digits the TS-Service-Centre-Address in EF_SMSP holds (TS 31.102): its 12 bytes are a length
 * byte, a TON/NPI byte and ten bytes of packed BCD. A longer number would be truncated and
 * written with a length byte that overruns the field.
 */
const SERVICE_CENTER_ADDRESS_MAX_DIGITS = 20;

@Component({
  selector: C.EWIZ_SMS_PARAMETERS_CONFIGURATION,
  templateUrl: "./sms-parameters-configuration.component.html",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ButtonModule,
    InputTextModule,
    MultiSelectModule,
    SelectModule,
    TableModule,
    SharedDirectivesModule,
    ErrorMessageComponent
  ]
})
export class EWizSMSParametersConfigurationComponent extends EWizDynamicBaseClassComponent implements OnInit, ErrorCheck, ProgressCheck {
  constructor(private dataService: DataService) {
    super();

    this.parameterIndicatorsOptions = [
      { label: $localize`Destination Address`, value: "1" },
      { label: $localize`Service Center Address`, value: "2" },
      { label: $localize`Protocol Identifier`, value: "3" },
      { label: $localize`Data Coding Scheme`, value: "4" },
      { label: $localize`Validity Period`, value: "5" }
    ];
    this.dataCodingSchemeOptions = [
      { label: $localize`(00) GSM 7bit default`, value: "1" },
      { label: $localize`(04) 8bit data`, value: "2" },
      { label: $localize`(08) UCS2 (16bit)`, value: "3" },
      { label: $localize`(F2) GSM 7bit default, (U)SIM specific message`, value: "4" },
      { label: $localize`(F6) 8bbit data, (U)SIM specific message`, value: "5" }
    ];

    for (let tpvpValue = 0; tpvpValue <= 255; tpvpValue++) {
      if (tpvpValue >= 0 && tpvpValue <= 143) {
        this.validityPeriodOptions.push({
          label: `${(tpvpValue + 1) * 5} ` + $localize`minutes`,
          value: `${tpvpValue}`
        });
      } else if (tpvpValue >= 144 && tpvpValue <= 167) {
        this.validityPeriodOptions.push({
          label: `${(12.0 + (((tpvpValue - 143) * 30) / 30) * 0.5).toFixed(1)} ` + $localize`hours`,
          value: `${tpvpValue}`
        });
      } else if (tpvpValue >= 168 && tpvpValue <= 196) {
        this.validityPeriodOptions.push({
          label: `${(tpvpValue - 166) * 1} ` + $localize`days`,
          value: `${tpvpValue}`
        });
      } else if (tpvpValue >= 197 && tpvpValue <= 255) {
        this.validityPeriodOptions.push({
          label: `${(tpvpValue - 192) * 1} ` + $localize`weeks`,
          value: `${tpvpValue}`
        });
      }
    }
  }

  /** Room for the digits plus the leading `+` of an international number. */
  protected readonly serviceCenterAddressMaxLength = SERVICE_CENTER_ADDRESS_MAX_DIGITS + 1;
  selectedParameters: ProfileModel.SMSParametersDto[] | null = null;
  nextId = 0;
  parameterIndicatorsOptions = [];
  dataCodingSchemeOptions = [];
  validityPeriodOptions = [];
  selectedItemsLabel = $localize`{0} indicator(s)`;

  ngOnInit() {
    this.errorCheck(this.profileValues, this.dataService);

    if (this.profileValues.smsParameters.length > 0) {
      for (const parameter of this.profileValues.smsParameters) {
        if (parameter.id > this.nextId) {
          this.nextId = parameter.id;
        }
      }
    }
  }

  errorCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    // Always reset error values to default state prior to executing check
    this.errorFound = false;
    this.errorMessage = "";

    if (values.smsParameters) {
      for (const parameter of values.smsParameters) {
        if (
          this.checkForIndicator(parameter, 3) &&
          !dataService.isNullOrEmpty(parameter.protocolIdentifier) &&
          !dataService.validHexValue(parameter.protocolIdentifier, 1, 1)
        ) {
          this.errorMessage = $localize`Protocol Indentifier values must be 1 HEX bytes in length`;
          this.errorFound = true;
        }

        if (
          this.checkForIndicator(parameter, 2) &&
          `${parameter.serviceCenterAddress ?? ""}`.replace(/\D/g, "").length > SERVICE_CENTER_ADDRESS_MAX_DIGITS
        ) {
          this.errorMessage = $localize`Service Center Address values must not exceed 20 digits`;
          this.errorFound = true;
        }
      }
    }

    return this.errorFound;
  }

  progressCheck(values: ProfileModel.ProfileDto, dataService: DataService) {
    for (const parameter of values.smsParameters) {
      if (this.checkForIndicator(parameter, 1) && dataService.isNullOrEmpty(parameter.destinationAddress)) {
        return false;
      } else if (this.checkForIndicator(parameter, 2) && dataService.isNullOrEmpty(parameter.serviceCenterAddress)) {
        return false;
      } else if (this.checkForIndicator(parameter, 3) && dataService.isNullOrEmpty(parameter.protocolIdentifier)) {
        return false;
      } else if (this.checkForIndicator(parameter, 4) && parameter.dataCodingScheme === null) {
        return false;
      } else if (this.checkForIndicator(parameter, 5) && parameter.validityPeriod === null) {
        return false;
      }
    }

    return true;
  }

  checkForIndicator(smsParameter: ProfileModel.SMSParametersDto, parameterIndicator: number) {
    for (const parameter of smsParameter.parameterIndicators) {
      if (+parameter.value === +parameterIndicator) {
        return true;
      }
    }

    return false;
  }

  addParameter() {
    const newParameters = [...this.profileValues.smsParameters];

    newParameters.push({
      id: ++this.nextId,
      name: null,
      parameterIndicators: [
        { label: $localize`Service Center Address`, value: "2" },
        { label: $localize`Protocol Identifier`, value: "3" },
        { label: $localize`Data Coding Scheme`, value: "4" }
      ],
      destinationAddress: null,
      serviceCenterAddress: null,
      protocolIdentifier: null,
      dataCodingScheme: null,
      validityPeriod: null
    });

    this.profileValues.smsParameters = [...newParameters];

    this.handleFormEvents();
  }

  deleteParameters() {
    const newParameters = [];

    for (const originalParameter of this.profileValues.smsParameters) {
      let removeParameter = false;

      for (const selectedParameter of this.selectedParameters) {
        if (originalParameter.id === selectedParameter.id) {
          removeParameter = true;
          break;
        }
      }

      if (!removeParameter) {
        newParameters.push(originalParameter);
      }
    }

    this.profileValues.smsParameters = [...newParameters];
    this.selectedParameters = [];

    this.handleFormEvents();
  }
}
